import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import {
  BillingCredit,
  CreateAndEditAccountRoleParams,
  getLeaderboardParams,
  getLeaderboardResponse,
  GetTeamMembersParams,
  GetTeamMembersResponse,
  GetTeamSummaryResponse,
  IAccountRolesRes,
  IChangePasswordBody,
  IJobTitlesRes,
  Invitation,
  InvitationListParams,
  InvitationsRes,
  IUpdateUserBody,
  IUpdateUserConfig,
  PermissionCategory,
  FarmOption,
  SendInvitationsBody,
  UpdateTeamMemberParams,
} from '@/modules/setting/types';
import { apiFetch } from '@/modules/shared/apiFetch';
import { IUserConfig } from '../authentication/types';

export function useUpdateUser(userId?: number) {
  return useMutation({
    // INFO: this actually return a User type with the property "isOnboardingVideoWatched"
    mutationFn: async ({ body }: { body: IUpdateUserBody }) => {
      if (!userId) return;
      await apiFetch(`/api/v1/users/${userId}`, {
        method: 'PATCH',
        body,
      });
    },
  });
}
export function useUpdateUserConfig(userId?: number) {
  return useMutation({
    mutationFn: async ({ body }: { body: IUpdateUserConfig }) => {
      if (!userId) return;
      return await apiFetch<IUserConfig>(`/api/v1/users/config/${userId}`, {
        method: 'PATCH',
        body,
      });
    },
  });
}
// ************************ Farm Brief — farm picker **************************
// Active farms only, already ordered by field count descending, so no sort here.
export function useGetFarmOptions(accountId?: number) {
  return useQuery({
    queryKey: ['farmOptions', accountId],
    enabled: !!accountId,
    queryFn: async () => {
      const data = await apiFetch<{ properties: FarmOption[]; total: number }>(
        `/api/v1/account-properties/lookup`,
        { method: 'GET', params: { accountId, page: 1, limit: 50 } }
      );
      return data.properties;
    },
  });
}

export function useGetJobTitles() {
  return useQuery({
    queryKey: ['Job_TITLES'],
    queryFn: async () => {
      return await apiFetch<IJobTitlesRes>(`/api/v1/job-titles`, {
        method: 'GET',
      });
    },
  });
}
export function useChangePassword() {
  return useMutation({
    mutationFn: async ({ body }: { body: IChangePasswordBody }) => {
      return await apiFetch<{ message: string }>(
        `/api/v1/auth/change-password/`,
        {
          method: 'POST',
          body,
        }
      );
    },
  });
}
export function useDeleteAccount(userId?: number) {
  return useMutation({
    mutationFn: async (body?: {
      transferOwnership?: boolean;
      transferOwnershipToUserId?: number;
    }) => {
      if (!userId) return;
      return await apiFetch<Record<string, never>>(`/api/v1/users/${userId}`, {
        method: 'DELETE',
        body: body ?? undefined,
      });
    },
  });
}
// ************************ Accounts Roles **************************
export function useGetAccountRoles() {
  return useQuery({
    queryKey: ['account-roles'],
    queryFn: async () => {
      return await apiFetch<IAccountRolesRes[]>(`/api/v1/account-roles`, {
        method: 'GET',
      });
    },
  });
}
// ************************ Invitations List **************************
export const useGetInvitationsList = (params: InvitationListParams) => {
  const { invitationType } = params;
  const searchTerm =
    params.invitationType === 'email' ? params.searchTerm : undefined;

  return useInfiniteQuery({
    queryKey: ['getInvitationsList', invitationType, searchTerm],
    queryFn: async ({ pageParam = 1 }) => {
      return await apiFetch<InvitationsRes>(`/api/v1/account-invitations`, {
        method: 'GET',
        params: { ...params, page: pageParam, limit: 10 },
      });
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage, allPages) => {
      const currentPage = allPages.length;
      const totalPages = Math.ceil(lastPage.total / 10);
      if (currentPage >= totalPages) {
        return undefined;
      }
      return currentPage + 1;
    },
    select: (data) => data.pages.flatMap((page) => page.invitations),
  });
};
// ************************ Send Invitations **************************
export const useSendInvitations = () => {
  return useMutation({
    mutationKey: ['send-invitations'],
    mutationFn: async (body: SendInvitationsBody) => {
      return await apiFetch<Invitation>(`/api/v1/account-invitations`, {
        method: 'POST',
        body,
      });
    },
  });
};
// ************************ Resend Invitations **************************
export const useResendInvitations = (id: string) => {
  return useMutation({
    mutationKey: ['resend-invitation'],
    mutationFn: async () => {
      await apiFetch(`/api/v1/account-invitations/${id}/resend`, {
        method: 'POST',
      });
    },
  });
};
// ************************ Cancel Invitations **************************
export const useCancelInvitations = (id: string) => {
  return useMutation({
    mutationKey: ['cancel-invitations'],
    mutationFn: async () => {
      await apiFetch(`/api/v1/account-invitations/${id}/cancel`, {
        method: 'PUT',
      });
    },
  });
};
// ************************ Get Permission Groups **************************
export function useGetPermissionGroups() {
  return useQuery({
    queryKey: ['group-permissions'],
    queryFn: async () => {
      return await apiFetch<PermissionCategory[]>(
        '/api/v1/account-roles/group-permissions',
        {
          method: 'GET',
        }
      );
    },
  });
}
// ************************ Create Custom Account Role **************************
export function useCreateAccountRole() {
  return useMutation({
    mutationFn: async (body: CreateAndEditAccountRoleParams) => {
      return await apiFetch('/api/v1/account-roles', {
        method: 'POST',
        body,
      });
    },
  });
}
// ************************ Update Custom Account Role **************************
export function useUpdateAccountRole() {
  return useMutation({
    mutationFn: async ({
      id,
      ...body
    }: CreateAndEditAccountRoleParams & { id: number }) => {
      return await apiFetch(`/api/v1/account-roles/${id}`, {
        method: 'PUT',
        body,
      });
    },
  });
}
// ************************ Delete Custom Account Role **************************
export function useDeleteAccountRole() {
  return useMutation({
    mutationFn: async (id: number) => {
      return await apiFetch(`/api/v1/account-roles/${id}`, {
        method: 'DELETE',
      });
    },
  });
}
// ************************ Get Team Members **************************
export function useGetTeamMembers(
  params: GetTeamMembersParams & { enabled?: boolean }
) {
  const limit = 20;
  const { enabled = true, ...apiParams } = params;
  return useInfiniteQuery({
    queryKey: ['getTeamMembers', apiParams],
    queryFn: async ({ pageParam }) => {
      return await apiFetch<GetTeamMembersResponse>('/api/v1/team-members', {
        method: 'GET',
        params: { ...apiParams, limit, page: pageParam },
      });
    },
    getNextPageParam: (lastPage, allPages) => {
      const currentPage = allPages.length;
      const totalPages = Math.ceil(lastPage.total / limit);
      if (currentPage >= totalPages) {
        return undefined;
      }
      return currentPage + 1;
    },
    initialPageParam: 1,
    select: (data) => data.pages.flatMap((page) => page.teamMembers),
    enabled,
  });
}
// ************************ Get Team Members Summary **************************
export const useGetTeamSummary = (enabled?: boolean) => {
  return useQuery({
    queryKey: ['getTeamSummary'],
    queryFn: async () => {
      const data = await apiFetch<GetTeamSummaryResponse>(
        '/api/v1/team-members/summary',
        { method: 'GET' }
      );
      return data;
    },
    enabled,
  });
};

// ************************ Get Billing Credit **************************
const EMPTY_BILLING_CREDIT: BillingCredit = {
  hasCredit: false,
  creditAmount: 0,
  balance: 0,
  currency: null,
  credits: [],
};
export const useGetBillingCredit = (enabled?: boolean) => {
  return useQuery({
    queryKey: ['getBillingCredit'],
    queryFn: async () => {
      try {
        return await apiFetch<BillingCredit>(
          '/api/v1/subscription/stripe/billing-credit',
          { method: 'GET' }
        );
      } catch (error) {
        // The endpoint is gated by BILLING_AND_SUBSCRIPTION_MANAGEMENT — a 403
        // means the caller can't see billing; treat it as "no credit" instead
        // of surfacing an error (see billing-credit API doc).
        if ((error as { statusCode?: number })?.statusCode === 403)
          return EMPTY_BILLING_CREDIT;
        throw error;
      }
    },
    enabled,
  });
};

// ************************ Make Team Member an Admin **************************
export function useMakeAdmin() {
  return useMutation({
    mutationFn: async (accountUserId: number) => {
      return await apiFetch(
        `/api/v1/team-members/${accountUserId}/make-admin`,
        {
          method: 'POST',
        }
      );
    },
  });
}
export function useTransferOwnership() {
  return useMutation({
    mutationFn: async (accountUserId: number) => {
      return await apiFetch(
        `/api/v1/team-members/${accountUserId}/transfer-ownership`,
        {
          method: 'POST',
        }
      );
    },
  });
}
// ************************ Edit Team Member **************************
export function useEditTeamMember() {
  const QueryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ accountUserId, ...body }: UpdateTeamMemberParams) => {
      return await apiFetch(`/api/v1/team-members/${accountUserId}`, {
        method: 'PATCH',
        body,
      });
    },
    onSettled: () =>
      QueryClient.invalidateQueries({
        queryKey: ['getTeamMembers'],
      }),
  });
}
// ************************ Delete Team Member **************************
export function useDeleteTeamMember() {
  return useMutation({
    mutationFn: async (accountUserId: number) => {
      return await apiFetch(`/api/v1/team-members/${accountUserId}`, {
        method: 'DELETE',
      });
    },
  });
}

export function useGetLeaderboard(
  params: getLeaderboardParams,
  refreshRef: React.RefObject<boolean>
) {
  return useInfiniteQuery({
    queryKey: ['getLeaderboard', params],
    queryFn: async ({ pageParam }) => {
      const shouldRefresh = refreshRef.current;
      if (refreshRef) refreshRef.current = false;
      return await apiFetch<getLeaderboardResponse>(
        `/api/v1/orth/leaderboard`,
        {
          method: 'GET',
          params: {
            ...params,
            page: pageParam,
            refresh: shouldRefresh || undefined,
          },
        }
      );
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage, allPages) => {
      const currentPage = allPages.length;
      const totalPages = Math.ceil(lastPage.total / params.limit);
      if (currentPage >= totalPages) return undefined;
      return currentPage + 1;
    },
    select: (data) => ({
      totalUsage: data.pages[0].totalUsage,
      members: data.pages.flatMap((page) => page.members),
      lastUpdated: data.pages[data.pages.length - 1].lastUpdated,
    }),
  });
}
